import { ChangeDetectionStrategy, Component, Input, OnInit } from '@angular/core';
import { MatLegacyDialog as MatDialog } from '@angular/material/legacy-dialog';
import { DialogDeleteGenericComponent } from '@app/shared/components/dialog-delete-generic/dialog-delete-generic.component';
import { ConnectedApp } from '@app/shared/interfaces/lib/connected-app.interface';
import { AppPermission, AppsService } from '@app/shared/services/apps/apps.service';
import { DeviceDetectionService } from '@app/shared/services/device-detection/device-detection.service';
import { IntercomService } from '@app/shared/services/intercom.service';
import { PosthogService } from '@app/shared/services/posthog/posthog.service';
import { sbnbIcon } from '@app/ui';
import { BehaviorSubject } from 'rxjs';

@Component({
  standalone: false,
  selector: 'sbnb-apps-detail-page',
  templateUrl: './apps-detail-page.component.html',
  styleUrls: ['./apps-detail-page.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AppsDetailPageComponent implements OnInit {
  @Input() vendorCode: string | null = null;
  @Input() logoTile: string | null = null;
  @Input() name: string | null = null;
  /**
   * Can turn off the description using the input or supply it using a named content projection slot:
   * <ng-container description>
   <p>
   Some description content
   </p>
   </ng-container>
   */
  @Input() description = true;
  @Input() beta = false;
  @Input() builtBy: string | null = null;
  @Input() builtByLink: string | null = null;
  @Input() categories: string[] | null = null;
  @Input() permissions: AppPermission[] | null = null;
  @Input() phone: string | null = null;
  @Input() email: string | null = null;
  @Input() helpLink: { href: string; text: string; icon?: sbnbIcon } | null = null;
  @Input() helpArticles: { id: string; title: string }[] | null = null;

  public connectedApp$ = new BehaviorSubject<null | ConnectedApp>(null);
  public isConnected$ = new BehaviorSubject<boolean>(false);
  public checking$ = new BehaviorSubject<boolean>(true);
  public generatingConnectionUrl$ = new BehaviorSubject<boolean>(false);

  constructor(
    private appsService: AppsService,
    public intercomService: IntercomService,
    private deviceDetectionService: DeviceDetectionService,
    private dialog: MatDialog,
    private posthog: PosthogService
  ) {}

  ngOnInit(): void {
    this.appsService.getIntegrationConnectionStatus(this.vendorCode).subscribe((app: null | ConnectedApp) => {
      this.checking$.next(false);

      if (app === null) {
        this.isConnected$.next(false);
        return;
      }
      this.isConnected$.next(true);
      this.connectedApp$.next(app);
    });
  }

  generateConnectionUrl() {
    this.generatingConnectionUrl$.next(true);

    this.appsService.generateIntegrationConnectionUrl(this.vendorCode).subscribe((redirectUri) => {
      this.generatingConnectionUrl$.next(false);

      if (redirectUri) {
        if (this.deviceDetectionService.isIosApp()) {
          // iOS will intercept and open in the system browser, but struggles with window.open
          window.location.href = redirectUri;
        } else {
          window.open(redirectUri, '_blank');
        }
      }
    });
  }

  deleteAppPrompt() {
    const dialogRef = this.dialog.open(DialogDeleteGenericComponent, {
      width: '720px',
      data: {
        name: this.name,
        action: 'Disconnect',
        additionalText:
          'Disconnecting this app will remove it from your account and the partner will no longer be able to access your data.',
      },
    });

    dialogRef.afterClosed().subscribe((result) => {
      if (result) {
        this.deleteApp();
      }
    });
  }

  deleteApp() {
    this.appsService.deleteIntegration(this.connectedApp$.value).subscribe((res) => {
      if (res) {
        this.connectedApp$.next(null);
        this.isConnected$.next(false);
      }
    });
  }
}
